import { getDomains } from '@/helpers/getDomains';
import { getV2StreamHeaders } from '@/utils/http/scribeSseHeaders';
import { logger } from '@/utils/logger/logger';
import { OFREPWebProvider } from '@openfeature/ofrep-web-provider';
import {
  type EvaluationContext,
  type EventContext,
  GeneralError,
  type JsonValue,
  OpenFeatureEventEmitter,
  type Provider,
  type ProviderEmittableEvents,
  ProviderEvents,
  ProviderNotReadyError,
  type ResolutionDetails,
} from '@openfeature/web-sdk';

export const createOfrepProvider = (): Provider => {
  const provider: OfrepProvider = new OfrepProvider({
    get baseUrl() {
      return `${getDomains().baseV2Url}feature-control`;
    },
    headersFactory: () => Promise.resolve(Object.entries(getV2StreamHeaders())),
    fetchImplementation: (...args) => provider.fetchForCurrentIdentity(...args),
    cacheMode: 'local-cache-first',
    cacheTTL: OFREP_CACHE_TTL_SECONDS,
    cacheKeyGenerator: ({ url, domain, targetingKey }) =>
      JSON.stringify([url, domain ?? '', targetingKey ?? '', ofrepCacheScope()]),
    timeoutMs: OFREP_TIMEOUT_MS,
  });
  return provider;
};

export class OfrepProvider extends OFREPWebProvider {
  override readonly events = new ShadowEventEmitter();
  private boundDomain?: string;
  private requestedKey?: string;
  private snapshot: OfrepSnapshot = 'none';
  private queue: Promise<void> = Promise.resolve();

  override initialize(context?: EvaluationContext, domain?: string): Promise<void> {
    this.boundDomain = domain;
    return context?.targetingKey ? this.enqueue(context.targetingKey) : Promise.resolve();
  }

  override onContextChange(
    _oldContext: EvaluationContext,
    newContext: EvaluationContext
  ): Promise<void> {
    return this.enqueue(newContext.targetingKey);
  }

  override resolveBooleanEvaluation(
    flagKey: string,
    defaultValue: boolean,
    context: EvaluationContext
  ): ResolutionDetails<boolean> {
    this.assertSnapshot();
    return super.resolveBooleanEvaluation(flagKey, defaultValue, context);
  }

  override resolveStringEvaluation(
    flagKey: string,
    defaultValue: string,
    context: EvaluationContext
  ): ResolutionDetails<string> {
    this.assertSnapshot();
    return super.resolveStringEvaluation(flagKey, defaultValue, context);
  }

  override resolveNumberEvaluation(
    flagKey: string,
    defaultValue: number,
    context: EvaluationContext
  ): ResolutionDetails<number> {
    this.assertSnapshot();
    return super.resolveNumberEvaluation(flagKey, defaultValue, context);
  }

  override resolveObjectEvaluation<T extends JsonValue>(
    flagKey: string,
    defaultValue: T,
    context: EvaluationContext
  ): ResolutionDetails<T> {
    this.assertSnapshot();
    return super.resolveObjectEvaluation(flagKey, defaultValue, context);
  }

  readonly fetchForCurrentIdentity = async (
    ...[input, init]: Parameters<typeof fetch>
  ): Promise<Response> => {
    const targetingKey = this.requestedKey;
    const response = await fetch(input, init);
    if (targetingKey === this.requestedKey) return response;
    void response.body?.cancel();
    logger.warn('[flags] OFREP refresh discarded', { reason: 'identity_changed' });
    // A 304 makes the SDK keep the current identity's flags instead of applying a stale refresh.
    return new Response(null, { status: 304 });
  };

  private enqueue(targetingKey?: string): Promise<void> {
    this.queue = this.queue.then(() => this.evaluate(targetingKey));
    return this.queue;
  }

  private async evaluate(targetingKey?: string): Promise<void> {
    if (this.snapshot === 'ready' && this.requestedKey === targetingKey) return;
    pruneOfrepCache(
      this.requestedKey !== undefined && this.requestedKey !== targetingKey
    );
    this.requestedKey = targetingKey;
    this.snapshot = 'pending';
    try {
      await super.initialize(targetingKey ? { targetingKey } : {}, this.boundDomain);
      this.snapshot = 'ready';
    } catch {
      this.snapshot = 'failed';
      logger.warn('[flags] OFREP unavailable', { failure: 'request_failed' });
    }
  }

  private assertSnapshot(): void {
    if (this.snapshot === 'ready') return;
    if (this.snapshot === 'failed') throw new GeneralError('OFREP unavailable');
    throw new ProviderNotReadyError('OFREP has no snapshot yet');
  }
}

// Everything the request sends except the rotating token: workspace, client and temp user.
const ofrepCacheScope = () =>
  Object.entries(getV2StreamHeaders())
    .filter(([name]) => name.toLowerCase() !== 'authorization')
    .sort(([a], [b]) => a.localeCompare(b));

// Under the 2s resolveFlagGate bound: readiness waits on both providers.
const OFREP_TIMEOUT_MS = 1_500;
const OFREP_CACHE_TTL_SECONDS = 7 * 24 * 60 * 60;

type OfrepSnapshot = 'none' | 'pending' | 'ready' | 'failed';

const OFREP_STORAGE_PREFIX = 'ofrep-web-provider:';

const isLiveOfrepEntry = (raw: string | null): boolean => {
  try {
    const writtenAt = Date.parse(
      (JSON.parse(raw ?? '') as { writtenAt?: string }).writtenAt ?? ''
    );
    return Date.now() - writtenAt < OFREP_CACHE_TTL_SECONDS * 1000;
  } catch {
    return false;
  }
};

// The library only evicts an entry when the same identity reads it again.
const pruneOfrepCache = (dropAll: boolean): void => {
  try {
    for (const key of Object.keys(localStorage)) {
      if (!key.startsWith(OFREP_STORAGE_PREFIX)) continue;
      if (dropAll || !isLiveOfrepEntry(localStorage.getItem(key)))
        localStorage.removeItem(key);
    }
  } catch {
    // Storage unavailable: nothing persisted to prune.
  }
};

// The shadow provider must never degrade the serving provider's status.
class ShadowEventEmitter extends OpenFeatureEventEmitter {
  override emit(event: ProviderEmittableEvents, context?: EventContext): void {
    if (event === ProviderEvents.Stale || event === ProviderEvents.Error) {
      logger.warn('[flags] OFREP unavailable', { failure: 'refresh_failed' });
      return;
    }
    super.emit(event, context);
  }
}
